import { useEffect, useRef, useCallback } from "react"
import type { TargetingGeography } from "../types"

const GEOGRAPHY_COUNTRIES: Record<TargetingGeography, string[]> = {
  "": [],
  "United States": [
    "US",
    "United States",
    "PR",
    "Puerto Rico",
    "United States Virgin Islands",
    "GU",
    "American Samoa",
    "Northern Mariana Islands",
  ],
  Canada: ["Canada", "CA"],
  "North America": [
    "US",
    "United States",
    "PR",
    "Puerto Rico",
    "United States Virgin Islands",
    "GU",
    "American Samoa",
    "Northern Mariana Islands",
    "Canada",
    "MX",
    "GT",
    "BZ",
    "SV",
    "HN",
    "NI",
    "CR",
    "PA",
    "CU",
    "JM",
    "HT",
    "DO",
    "Bahamas",
    "Cayman Islands",
    "Trinidad and Tobago",
    "BB",
    "DM",
    "GD",
    "LC",
    "Saint Kitts and Nevis",
    "VC",
    "Antigua and Barbuda",
    "AI",
    "MS",
    "VG",
    "AW",
    "CW",
    "Turks and Caicos Islands",
    "Guadeloupe",
    "MQ",
    "BL",
    "MF",
    "SX",
    "GL",
  ],
  Europe: [
    "AT",
    "BE",
    "BG",
    "BA",
    "BY",
    "CH",
    "CZ",
    "DE",
    "Denmark",
    "EE",
    "ES",
    "FI",
    "France",
    "United Kingdom",
    "Greece",
    "HR",
    "HU",
    "IE",
    "IS",
    "Italy",
    "LT",
    "LU",
    "LV",
    "MD",
    "MK",
    "ME",
    "NL",
    "Norway",
    "PL",
    "PT",
    "RO",
    "RS",
    "SE",
    "SI",
    "SK",
    "UA",
    "AL",
    "XK",
    "Cyprus",
    "Malta",
    "Faeroe Islands",
    "Canary Islands (Spain)",
    "Turkey",
  ],
  Global: [],
}

// Full SVG dimensions
const SVG_W = 2000
const SVG_H = 857

// Region focus areas: { centerX, centerY, zoom }
const GEOGRAPHY_FOCUS: Record<
  TargetingGeography,
  { cx: number; cy: number; zoom: number }
> = {
  "": { cx: SVG_W / 2, cy: SVG_H / 2, zoom: 1 },
  "United States": { cx: 500, cy: 250, zoom: 2.5 },
  Canada: { cx: 500, cy: 180, zoom: 2.5 },
  "North America": { cx: 500, cy: 250, zoom: 2 },
  Europe: { cx: 1075, cy: 180, zoom: 3.5 },
  Global: { cx: SVG_W / 2, cy: SVG_H / 2, zoom: 1 },
}

interface WorldMapSelectorProps {
  value: TargetingGeography
  onChange: (geo: TargetingGeography) => void
}

export default function WorldMapSelector({
  value,
  onChange,
}: WorldMapSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgLoaded = useRef(false)
  const wrapperGRef = useRef<SVGGElement | null>(null)

  useEffect(() => {
    if (svgLoaded.current || !containerRef.current) return
    svgLoaded.current = true

    fetch("/world.svg")
      .then(r => r.text())
      .then(svgText => {
        if (!containerRef.current) return
        containerRef.current.innerHTML = svgText

        const svg = containerRef.current.querySelector("svg")
        if (svg) {
          svg.setAttribute("width", "100%")
          svg.setAttribute("height", "100%")
          svg.setAttribute("viewBox", `0 0 ${SVG_W} ${SVG_H}`)
          svg.removeAttribute("baseprofile")
          svg.style.overflow = "hidden"

          // Wrap all children in a <g> for transform-based zoom
          const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
          g.style.transition = "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)"
          g.style.transformOrigin = "0 0"
          while (svg.firstChild) {
            g.appendChild(svg.firstChild)
          }
          svg.appendChild(g)
          wrapperGRef.current = g

          // Style all paths
          svg.querySelectorAll("path").forEach(path => {
            path.style.fill = "rgb(30, 35, 50)"
            path.style.stroke = "rgb(50, 55, 70)"
            path.style.strokeWidth = "0.3"
            path.style.transition = "fill 0.3s ease"
          })

          // Remove circles
          svg.querySelectorAll("circle").forEach(c => c.remove())
        }
      })
  }, [])

  const highlightCountries = useCallback(
    (countries: string[], svg: SVGElement) => {
      for (const country of countries) {
        svg.querySelectorAll(`[id="${country}"]`).forEach(el => {
          const svgEl = el as SVGElement
          svgEl.style.fill = "#EB89B5"
        })
        svg.querySelectorAll(`[class="${country}"]`).forEach(el => {
          const svgEl = el as SVGElement
          svgEl.style.fill = "#EB89B5"
        })
      }
    },
    [],
  )

  const applyZoom = useCallback(
    (focus: { cx: number; cy: number; zoom: number }) => {
      const g = wrapperGRef.current
      if (!g) return

      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      // Map container aspect ratio to SVG coordinate space
      const containerAspect = rect.width / rect.height
      const svgAspect = SVG_W / SVG_H
      let visibleW = SVG_W
      let visibleH = SVG_H
      if (containerAspect > svgAspect) {
        visibleH = SVG_H
        visibleW = SVG_H * containerAspect
      } else {
        visibleW = SVG_W
        visibleH = SVG_W / containerAspect
      }

      const { cx, cy, zoom } = focus
      // Translate so that (cx, cy) is at the center of the visible area, then scale
      const tx = visibleW / 2 / zoom - cx
      const ty = visibleH / 2 / zoom - cy

      g.style.transform = `scale(${zoom}) translate(${tx}px, ${ty}px)`
    },
    [],
  )

  useEffect(() => {
    const svg = containerRef.current?.querySelector("svg")
    if (!svg) return

    // Reset all paths
    svg.querySelectorAll("path").forEach(path => {
      path.style.fill = "rgb(30, 35, 50)"
      path.style.opacity = "1"
    })

    // Apply highlights
    if (value === "Global") {
      svg.querySelectorAll("path").forEach(path => {
        path.style.fill = "#EB89B5"
        path.style.opacity = "0.8"
      })
    } else if (value) {
      const countries = GEOGRAPHY_COUNTRIES[value]
      if (countries) highlightCountries(countries, svg)
    }

    // Apply zoom
    const focus = GEOGRAPHY_FOCUS[value] || GEOGRAPHY_FOCUS[""]
    applyZoom(focus)
  }, [value, highlightCountries, applyZoom])

  const geos: TargetingGeography[] = [
    "United States",
    "Canada",
    "North America",
    "Europe",
    "Global",
  ]

  return (
    <div className="space-y-2">
      <div
        ref={containerRef}
        className="w-full rounded-lg overflow-hidden bg-system-gray-900/50 border border-system-gray-700/30 p-2"
        style={{ minHeight: "200px" }}
      />
      <div className="flex flex-col gap-1.5">
        {geos.map(geo => {
          const isSelected = value === geo
          return (
            <button
              key={geo}
              type="button"
              onClick={() => onChange(geo)}
              className={`w-full py-1.5 px-3 rounded-lg text-xs font-medium text-left transition-all ${
                isSelected
                  ? "bg-brand/20 text-brand border border-brand/40"
                  : "bg-system-gray-800/50 text-basic-light/50 border border-system-gray-700/30 hover:border-system-gray-600/50"
              }`}
            >
              {geo}
            </button>
          )
        })}
      </div>
    </div>
  )
}
